import React from 'react';

import {
  Button,
  Drawer,
  Pagination,
  Select,
  Table,
  TableThumb,
  Toolbar,
} from '../../../design-system/components/desktop';
import {
  ActionRow,
  ConfirmDialog,
  ConsoleEmpty,
  ConsoleError,
  DetailList,
  DetailRow,
  DetailSection,
  StatusPill,
} from '../../../design-system/patterns/console';
import { formatCurrency, mediaUrl } from '../../../utils/common';
import { finishLabel } from '../../../utils/finish';
import { routes } from '../../../routes/routes';

import '../../Dashboard/dashboard.css';

/**
 * "My listings", the view. Pure.
 *
 * A SELLER'S OWN LISTINGS ARE MANAGING, SO THIS IS A TABLE, per `README.md`
 * section 7. Cards are for browsing and desiring; this surface exists so a
 * seller can find the one row they need to edit or delete.
 *
 * WHAT DIED HERE
 * --------------
 * - `GenericTable`, its `renderHeader` escape hatch, its bundled pagination and
 *   its hand-rolled `admin-table-skeleton`.
 * - `TableFilters`, a shared header component that took nineteen props and
 *   decided which of them to render by counting which setters were passed.
 * - `ListingTable.css`, and the `#FFFFFF` hardcoded on every single cell's
 *   inline style, which made every value invisible in light theme.
 * - Two hand-rolled select-all checkbox blocks, one desktop and one mobile,
 *   with a `useEffect` each to set `indeterminate`. `Table` owns selection now
 *   and sets `indeterminate` once, on one box.
 * - The detail `Modal` with its `.btn-gradient` close button. A record detail
 *   is a `Drawer` in this system.
 * - `ConfirmationPopup`, replaced by `ConfirmDialog`.
 *
 * DELETE IS A GHOST BUTTON. `.dsk-btn--danger` does not exist. The label
 * carries the consequence ("Delete listing", "Delete 3 listings") and a real
 * confirmation does the work.
 */

const SORT_BY_OPTIONS = [
  { value: 'created_at', label: 'Sort by created' },
];

const SORT_ORDER_OPTIONS = [
  { value: 'desc', label: 'Newest first' },
  { value: 'asc', label: 'Oldest first' },
];

/** The API sends `image_url` as an array on the list and a string on a record. */
function firstImage(row) {
  const raw = Array.isArray(row?.image_url) ? row.image_url[0] : row?.image_url;
  return raw ? mediaUrl(raw) : undefined;
}

export function ListingTableView({
  rows = [],
  loading = false,
  error,
  onRetry,

  search = '',
  onSearchChange,
  status = 'All',
  onStatusChange,
  statusOptions = [],
  sortBy = 'created_at',
  onSortByChange,
  sortOrder = 'asc',
  onSortOrderChange,

  page = 1,
  pageCount = 1,
  pageSize = 10,
  total = 0,
  onPageChange,

  selectedIds = [],
  onSelectionChange,
  onBulkDelete,
  bulkConfirmOpen = false,
  onBulkConfirmClose,
  onBulkConfirm,
  bulkBusy = false,

  viewing = null,
  onView,
  onCloseView,

  onEdit,
  editModal = null,

  deleting = null,
  onDelete,
  onDeleteCancel,
  onDeleteConfirm,
  deleteBusy = false,
}) {
  const filtered = Boolean(search) || (status && status !== 'All');

  const columns = [
    {
      key: 'card',
      header: 'Card',
      render: (row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          <TableThumb src={firstImage(row)} alt="" />
          <span>{row.card}</span>
        </span>
      ),
      // A 40px compact row cannot hold a 40x54 thumb, so compact drops it
      // rather than shrinking it. The image is repeated in the drawer.
    },
    { key: 'rarity', header: 'Rarity', muted: true, render: (row) => row.rarity || 'Not stated' },
    { key: 'condition', header: 'Condition', muted: true },
    /* FINISH. The column that tells two listings of one card apart — without
       it a Reverse Holofoil and a Normal copy render identically in every
       visible field (same art, name, rarity, condition) and choosing which to
       edit is guesswork. Always prints: a blank cell under a heading claims
       "unknown", which the data never justifies. `finish` is populated on
       every listing, defaulted to "Normal" by the model. */
    {
      key: 'finish',
      header: 'Finish',
      muted: true,
      render: (row) => finishLabel(row.finish),
    },
    {
      key: 'bought_at',
      header: 'You paid',
      numeric: true,
      render: (row) => formatCurrency(row.bought_at),
    },
    { key: 'qty', header: 'Qty', numeric: true },
    {
      key: 'created_at',
      header: 'Created',
      muted: true,
      render: (row) =>
        row.created_at ? new Date(row.created_at).toLocaleDateString('en-ZA') : 'Unknown',
    },
    { key: 'status', header: 'Status', render: (row) => <StatusPill status={row.status} /> },
  ];

  return (
    <>
      <Table
        columns={columns}
        data={error ? [] : rows}
        rowKey="list_id"
        loading={loading}
        selectable
        selectedKeys={selectedIds}
        onSelectionChange={onSelectionChange}
        selectAllLabel="Select every listing on this page"
        selectRowLabel={(row) => `Select ${row.card}`}
        onRowClick={(row) => onView && onView(row)}
        emptyState={
          error ? (
            <ConsoleError error={error} onRetry={onRetry} title="Could not load your listings" />
          ) : (
            <ConsoleEmpty
              filtered={filtered}
              illustration="no-listings-yet"
              title="You have no listings yet"
              text="Every card you list for sale shows up here with its status."
              filteredText="No listing matches that search or status. Clear one of them and your listings come back."
              /* The worst of the ten actionless empty states: a seller's own
                 dashboard telling them they have sold nothing and offering no
                 way to start. `/create-listing` is a real route. */
              action={
                <Button as="a" href={routes.CREATE_LISTING} size="sm">
                  Create your first listing
                </Button>
              }
              onClear={() => {
                if (onSearchChange) onSearchChange('');
                if (onStatusChange) onStatusChange('All');
              }}
            />
          )
        }
        rowActions={(row) => (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                onView(row);
              }}
            >
              View
            </Button>
            {/* Editing is only offered where the API allows it, exactly as
                before: a listing still awaiting review has nothing to edit. */}
            {(row.status === 'approved' || row.status === 'rejected') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit(row);
                }}
              >
                Edit
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                onDelete(row);
              }}
            >
              Delete listing
            </Button>
          </>
        )}
        toolbar={
          <Toolbar
            search={search}
            onSearchChange={onSearchChange}
            searchPlaceholder="Filter by card name"
            searchLabel="Filter your listings"
            count={loading ? undefined : `${total} ${total === 1 ? 'listing' : 'listings'}`}
            filters={
              <div className="dsa-dash__filters">
                <Select
                  options={statusOptions}
                  value={status}
                  onChange={onStatusChange}
                  placeholder="All statuses"
                  label="Filter by status"
                />
                <Select
                  options={SORT_BY_OPTIONS}
                  value={sortBy}
                  onChange={onSortByChange}
                  label="Sort listings by"
                />
                <Select
                  options={SORT_ORDER_OPTIONS}
                  value={sortOrder}
                  onChange={onSortOrderChange}
                  label="Sort direction"
                />
              </div>
            }
            chips={
              selectedIds.length > 0
                ? [
                    {
                      id: 'selection',
                      label: `${selectedIds.length} selected`,
                      removeLabel: 'Clear the selection',
                      onRemove: () => onSelectionChange && onSelectionChange([]),
                    },
                  ]
                : []
            }
          />
        }
        pagination={
          pageCount > 1 ? (
            <Pagination
              page={page}
              pageCount={pageCount}
              pageSize={pageSize}
              totalItems={total}
              itemNoun="listings"
              onPageChange={onPageChange}
            />
          ) : null
        }
      />

      {/* The bulk command sits under the table rather than in the toolbar,
          because it only exists once rows are chosen and a control that
          appears inside a filter row reads as a filter. */}
      {selectedIds.length > 0 ? (
        <ActionRow start style={{ marginTop: 'var(--ds-space-4, 16px)' }}>
          <Button variant="ghost" onClick={onBulkDelete}>
            Delete {selectedIds.length} listing{selectedIds.length > 1 ? 's' : ''}
          </Button>
        </ActionRow>
      ) : null}

      <Drawer
        open={Boolean(viewing) && !deleting && !bulkConfirmOpen}
        onClose={onCloseView}
        kicker="Listing"
        title={viewing?.card || 'Listing'}
        footer={
          viewing ? (
            <ActionRow>
              <Button
                variant="ghost"
                onClick={() => {
                  onCloseView();
                  onDelete(viewing);
                }}
              >
                Delete listing
              </Button>
              {(viewing.status === 'approved' || viewing.status === 'rejected') && (
                <Button
                  variant="primary"
                  onClick={() => {
                    onCloseView();
                    onEdit(viewing);
                  }}
                >
                  Edit listing
                </Button>
              )}
            </ActionRow>
          ) : null
        }
      >
        {viewing ? (
          <>
            {firstImage(viewing) ? (
              <DetailSection>
                <img
                  src={firstImage(viewing)}
                  alt={viewing.card}
                  style={{ maxWidth: '100%', borderRadius: 'var(--ds-radius-md, 12px)' }}
                />
              </DetailSection>
            ) : null}

            <DetailSection>
              <DetailList>
                <DetailRow label="Status" value={<StatusPill status={viewing.status} />} keepEmpty />
                <DetailRow label="Rarity" value={viewing.rarity} />
                <DetailRow label="Condition" value={viewing.condition} />
                <DetailRow label="Finish" value={finishLabel(viewing.finish)} />
                <DetailRow label="You paid" value={formatCurrency(viewing.bought_at)} />
                <DetailRow label="Quantity" value={viewing.qty} />
                <DetailRow
                  label="Created"
                  value={
                    viewing.created_at
                      ? new Date(viewing.created_at).toLocaleDateString('en-ZA')
                      : undefined
                  }
                />
              </DetailList>
            </DetailSection>

            {viewing.tags && viewing.tags.length > 0 ? (
              <DetailSection title="Tags">
                <div className="dsa-dash__filters">
                  {viewing.tags.map((tag) => (
                    <span className="dsk-pill" key={tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              </DetailSection>
            ) : null}
          </>
        ) : null}
      </Drawer>

      {editModal}

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={onDeleteCancel}
        onConfirm={onDeleteConfirm}
        title="Delete listing"
        confirmLabel="Delete listing"
        busy={deleteBusy}
        busyLabel="Deleting"
      >
        <p>
          <strong>{deleting?.card || 'This listing'}</strong> will be removed from the marketplace.
          This cannot be undone.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={bulkConfirmOpen}
        onClose={onBulkConfirmClose}
        onConfirm={onBulkConfirm}
        title="Delete selected listings"
        confirmLabel={`Delete ${selectedIds.length} listing${selectedIds.length > 1 ? 's' : ''}`}
        busy={bulkBusy}
        busyLabel="Deleting"
      >
        <p>
          {selectedIds.length} listing{selectedIds.length > 1 ? 's' : ''} will be removed from the
          marketplace. This cannot be undone.
        </p>
      </ConfirmDialog>
    </>
  );
}

export default ListingTableView;
